import { pl } from './locales/pl.js'
import { cs } from './locales/cs.js'
import { sk } from './locales/sk.js'
import { sl } from './locales/sl.js'
import { da } from './locales/da.js'
import { et } from './locales/et.js'
import { lt } from './locales/lt.js'
import { lv } from './locales/lv.js'
import { mk } from './locales/mk.js'
import { sr } from './locales/sr.js'
import { uk } from './locales/uk.js'
import { bg } from './locales/bg.js'
import { hy } from './locales/hy.js'
import { ka } from './locales/ka.js'
import { is } from './locales/is.js'
import { ga } from './locales/ga.js'
import { hu } from './locales/hu.js'
import { ro } from './locales/ro.js'
import { fa } from './locales/fa.js'
import { vi } from './locales/vi.js'
import { ca } from './locales/ca.js'
import { cy } from './locales/cy.js'
import { eu } from './locales/eu.js'
import { gl } from './locales/gl.js'
import { pcm } from './locales/pcm.js'
import { BASQUE_COUNTRY, CATALONIA, GALICIA } from './flags.js'
import { EGYPTIAN_ARABIC, NAIJA } from './exonyms.js'
import { af } from './locales/af.js'
import { ar } from './locales/ar.js'
import { arz } from './locales/arz.js'
import { de } from './locales/de.js'
import { el } from './locales/el.js'
import { en } from './locales/en.js'
import { es } from './locales/es.js'
import { fi } from './locales/fi.js'
import { fr } from './locales/fr.js'
import { he } from './locales/he.js'
import { hr } from './locales/hr.js'
import { id } from './locales/id.js'
import { it } from './locales/it.js'
import { ja } from './locales/ja.js'
import { ko } from './locales/ko.js'
import { la } from './locales/la.js'
import { ms } from './locales/ms.js'
import { nl } from './locales/nl.js'
import { no } from './locales/no.js'
import { pt } from './locales/pt.js'
import { ptBR } from './locales/pt-BR.js'
import { ru } from './locales/ru.js'
import { sv } from './locales/sv.js'
import { sw } from './locales/sw.js'
import { tl } from './locales/tl.js'
import { tr } from './locales/tr.js'
import type { Messages } from './messages.js'

export interface Locale {
  /** BCP 47 tag, and the same id the engine uses for the alphabet. */
  readonly tag: string
  /** The language's own name for itself, so a speaker can find it. */
  readonly endonym: string
  /**
   * A flag, which is a compromise and worth naming as one. A flag is a country and these are
   * languages, so the pairing is arbitrary wherever a language has more than one home. It is
   * here because it makes a picker scannable at a glance in a way a column of names in as many
   * scripts is not, and the endonym next to it carries the actual meaning.
   */
  readonly flag: string
  /**
   * The language whose name to file this one under, where a browser has no name of its own
   * for it.
   *
   * The picker sorts by what the *reader* calls each language, through `Intl.DisplayNames`, so
   * that the list is alphabetical to whoever is reading it rather than ranked by Unicode block.
   * ICU does not name every language: a browser knows no name for `arz` in any locale, and none
   * for `pcm` either, so both would fall back to their endonym and sink to the bottom of a Latin
   * list — which is the thing the sort exists to stop.
   *
   * Filing Egyptian Arabic under Arabic puts it next to Arabic, which is where somebody looking
   * for it would look. Absent for every language ICU can name, which is all but one of these.
   */
  readonly sortsWith?: string
  /**
   * What each interface language calls this one, where ICU calls it nothing.
   *
   * `Intl.DisplayNames` names forty-nine of the fifty-one everywhere, and Nigerian Pidgin and
   * Egyptian Arabic only if the runtime's CLDR is new enough: Chrome 123 names neither in any
   * locale, Node 26 names both. So a name here **overrides** ICU rather than filling in behind
   * it, because the alternative is a list that sorts differently after a browser update.
   *
   * It is drawn under the endonym and it is what the list is sorted by, which is why neither
   * language needs filing beside a neighbour any more. See exonyms.ts.
   */
  readonly namedIn?: Readonly<Record<string, string>>
  /**
   * Whether this language can be played, which is a fact about a word list rather than about a
   * translation.
   *
   * True only when an attested list for it has been borrowed into `packages/words/data` and
   * proven to deal a board somebody could play: three seeds on the board the game opens on,
   * each needing an accepted board holding a word of six tiles. `pnpm languages update` is what
   * sets this, and it refuses to turn one off without an operator saying so by name.
   *
   * A localization keeps its place here whether or not this is true. Translating the interface
   * into a language is finished work, and a language with no dictionary yet is waiting for
   * evidence, not for a translator. So the flag hides it from the board, never from the repo.
   */
  readonly available: boolean
  readonly messages: Messages
}

/**
 * Every language the interface is translated into, in the order a picker shows them.
 *
 * Not the same as the languages that can be *played*: that also needs a word list, which is
 * what `available` records. Keeping the two apart is deliberate, because the game language and
 * the interface language are separate settings, and a translation does not stop being finished
 * work because nobody has attested a dictionary for it yet.
 */
export const LOCALES: readonly Locale[] = [
  // English takes the Union flag for the language's origin rather than for a dialect: the
  // English word list is the union of en-US and en-GB, so COLOR and COLOR both play.
  { tag: 'en', endonym: 'English', flag: '🇬🇧', available: true, messages: en },
  { tag: 'fr', endonym: 'Français', flag: '🇫🇷', available: true, messages: fr },
  { tag: 'es', endonym: 'Español', flag: '🇪🇸', available: true, messages: es },
  { tag: 'it', endonym: 'Italiano', flag: '🇮🇹', available: true, messages: it },
  { tag: 'de', endonym: 'Deutsch', flag: '🇩🇪', available: true, messages: de },
  { tag: 'nl', endonym: 'Nederlands', flag: '🇳🇱', available: true, messages: nl },
  { tag: 'af', endonym: 'Afrikaans', flag: '🇿🇦', available: true, messages: af },
  { tag: 'sw', endonym: 'Kiswahili', flag: '🇹🇿', available: true, messages: sw },
  // The two that read the other way. Israel for Hebrew; Saudi Arabia for Arabic, which is a
  // flag for a language spoken across twenty-odd countries and the usual compromise.
  { tag: 'he', endonym: 'עברית', flag: '🇮🇱', available: true, messages: he },
  { tag: 'ar', endonym: 'العربية', flag: '🇸🇦', available: true, messages: ar },
  {
    tag: 'arz',
    endonym: 'مصرى',
    flag: '🇪🇬',
    namedIn: EGYPTIAN_ARABIC,
    available: false,
    messages: arz,
  },
  { tag: 'pt', endonym: 'Português', flag: '🇵🇹', available: true, messages: pt },
  { tag: 'pt-BR', endonym: 'Português (Brasil)', flag: '🇧🇷', available: true, messages: ptBR },
  { tag: 'hr', endonym: 'Hrvatski', flag: '🇭🇷', available: true, messages: hr },
  { tag: 'ms', endonym: 'Bahasa Melayu', flag: '🇲🇾', available: true, messages: ms },
  { tag: 'id', endonym: 'Bahasa Indonesia', flag: '🇮🇩', available: true, messages: id },
  { tag: 'ja', endonym: '日本語', flag: '🇯🇵', available: false, messages: ja },
  { tag: 'ko', endonym: '한국어', flag: '🇰🇷', available: true, messages: ko },
  // Filipino by law and Tagalog by name: the flag is the country, the endonym the language.
  { tag: 'tl', endonym: 'Tagalog', flag: '🇵🇭', available: true, messages: tl },
  { tag: 'ru', endonym: 'Русский', flag: '🇷🇺', available: true, messages: ru },
  { tag: 'sv', endonym: 'Svenska', flag: '🇸🇪', available: true, messages: sv },
  { tag: 'no', endonym: 'Norsk', flag: '🇳🇴', available: true, messages: no },
  { tag: 'fi', endonym: 'Suomi', flag: '🇫🇮', available: true, messages: fi },
  { tag: 'el', endonym: 'Ελληνικά', flag: '🇬🇷', available: true, messages: el },
  { tag: 'tr', endonym: 'Türkçe', flag: '🇹🇷', available: true, messages: tr },
  { tag: 'pl', endonym: 'Polski', flag: '🇵🇱', available: true, messages: pl },
  { tag: 'cs', endonym: 'Čeština', flag: '🇨🇿', available: true, messages: cs },
  { tag: 'sk', endonym: 'Slovenčina', flag: '🇸🇰', available: true, messages: sk },
  { tag: 'sl', endonym: 'Slovenščina', flag: '🇸🇮', available: true, messages: sl },
  { tag: 'da', endonym: 'Dansk', flag: '🇩🇰', available: true, messages: da },
  { tag: 'et', endonym: 'Eesti', flag: '🇪🇪', available: true, messages: et },
  { tag: 'lt', endonym: 'Lietuvių', flag: '🇱🇹', available: true, messages: lt },
  { tag: 'lv', endonym: 'Latviešu', flag: '🇱🇻', available: true, messages: lv },
  { tag: 'mk', endonym: 'Македонски', flag: '🇲🇰', available: true, messages: mk },
  { tag: 'sr', endonym: 'Српски', flag: '🇷🇸', available: true, messages: sr },
  { tag: 'uk', endonym: 'Українська', flag: '🇺🇦', available: true, messages: uk },
  { tag: 'bg', endonym: 'Български', flag: '🇧🇬', available: true, messages: bg },
  { tag: 'hy', endonym: 'Հայերեն', flag: '🇦🇲', available: true, messages: hy },
  { tag: 'ka', endonym: 'ქართული', flag: '🇬🇪', available: true, messages: ka },
  { tag: 'is', endonym: 'Íslenska', flag: '🇮🇸', available: true, messages: is },
  { tag: 'ga', endonym: 'Gaeilge', flag: '🇮🇪', available: true, messages: ga },
  { tag: 'hu', endonym: 'Magyar', flag: '🇭🇺', available: true, messages: hu },
  { tag: 'ro', endonym: 'Română', flag: '🇷🇴', available: true, messages: ro },
  { tag: 'fa', endonym: 'فارسی', flag: '🇮🇷', available: true, messages: fa },
  { tag: 'vi', endonym: 'Tiếng Việt', flag: '🇻🇳', available: true, messages: vi },
  // Welsh gets a real emoji: Unicode has flags for England, Scotland and Wales and no other
  // subdivision anywhere, which is why the next three are drawn instead. See flags.ts.
  { tag: 'cy', endonym: 'Cymraeg', flag: '🏴󠁧󠁢󠁷󠁬󠁳󠁿', available: true, messages: cy },
  { tag: 'ca', endonym: 'Català', flag: CATALONIA, available: true, messages: ca },
  { tag: 'eu', endonym: 'Euskara', flag: BASQUE_COUNTRY, available: true, messages: eu },
  { tag: 'gl', endonym: 'Galego', flag: GALICIA, available: true, messages: gl },
  { tag: 'pcm', endonym: 'Naijá', flag: '🇳🇬', namedIn: NAIJA, available: true, messages: pcm },
  // Last, and the one flag here that is a country nobody lives in.
  { tag: 'la', endonym: 'Latina', flag: '🇻🇦', available: false, messages: la },
]

export const DEFAULT_LOCALE = 'en'

const BY_TAG: ReadonlyMap<string, Locale> = new Map(
  LOCALES.map((locale) => [locale.tag, locale] as const),
)

export function localeFor(tag: string): Locale | undefined {
  return BY_TAG.get(tag)
}

/**
 * The languages a board can be dealt in, in the order a picker shows them.
 *
 * This and `LOCALES` answer two different questions and the pickers must not confuse them. A
 * player choosing the language of the *game* is choosing a dictionary, and offering one that
 * does not exist deals a board with nothing on it. A player choosing the language of the
 * *interface* is choosing what the buttons say, which needs no dictionary at all.
 */
export const PLAYABLE: readonly Locale[] = LOCALES.filter((locale) => locale.available)

/** Whether a board can be dealt in this language. An unknown tag cannot. */
export function isPlayable(tag: string): boolean {
  return BY_TAG.get(tag)?.available ?? false
}

/**
 * The messages for a tag, falling back to English.
 *
 * Falls back rather than throwing because the interface language can come from a stale
 * setting or a browser preference, and an untranslated interface is a far better outcome than
 * a blank screen.
 */
export function messagesFor(tag: string): Messages {
  return BY_TAG.get(tag)?.messages ?? en
}

/**
 * Best interface language for a set of browser preferences.
 *
 * Matches the exact tag first, then the base language, so `pt-PT` finds Portuguese and
 * `pt-BR` finds Brazilian rather than both landing on whichever came first. A regional tag we
 * have no entry for still finds its language.
 */
export function preferredLocale(preferences: readonly string[]): string {
  for (const preference of preferences) {
    const exact = LOCALES.find((locale) => locale.tag.toLowerCase() === preference.toLowerCase())
    if (exact !== undefined) return exact.tag
    const base = preference.split('-')[0]?.toLowerCase()
    const loose = LOCALES.find((locale) => locale.tag.split('-')[0]?.toLowerCase() === base)
    if (loose !== undefined) return loose.tag
  }
  return DEFAULT_LOCALE
}
